import { Menu as Primitive } from '@base-ui/react/menu';
import { Separator as SeparatorPrimitive } from '@base-ui/react/separator';
import { cn } from '@foundation/utils/cn';
import type { ComponentProps, ReactElement, ReactNode } from 'react';

export type HeadlessMenuRootProps = Readonly<Primitive.Root.Props>;
export type HeadlessMenuTriggerProps = Omit<
  Readonly<Primitive.Trigger.Props>,
  'children' | 'render' | 'className' | 'style'
> & {
  readonly children: ReactElement;
};

export type HeadlessMenuPopupProps = Omit<
  Readonly<Primitive.Popup.Props>,
  'className'
> & {
  readonly className?: string;
  readonly side?: Primitive.Positioner.Props['side'];
  readonly align?: Primitive.Positioner.Props['align'];
  readonly sideOffset?: number;
};

export type HeadlessMenuItemProps = Omit<
  Readonly<Primitive.Item.Props>,
  'className'
> & {
  readonly className?: string;
  readonly startAdornment?: ReactNode;
  readonly endAdornment?: ReactNode;
};

export type HeadlessMenuGroupProps = Readonly<
  ComponentProps<typeof Primitive.Group>
>;
export type HeadlessMenuGroupLabelProps = Readonly<
  ComponentProps<typeof Primitive.GroupLabel>
>;
export type HeadlessMenuSeparatorProps = Readonly<
  ComponentProps<typeof SeparatorPrimitive>
>;

function Trigger({ children, ...props }: HeadlessMenuTriggerProps) {
  return (
    <Primitive.Trigger
      {...props}
      render={children}
    />
  );
}

function Popup({
  side = 'bottom',
  align = 'end',
  sideOffset = 8,
  className,
  ...props
}: HeadlessMenuPopupProps) {
  return (
    <Primitive.Portal>
      <Primitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        className="z-50"
      >
        <Primitive.Popup
          {...props}
          className={className}
        />
      </Primitive.Positioner>
    </Primitive.Portal>
  );
}

function Item({
  children,
  startAdornment,
  endAdornment,
  className,
  ...props
}: HeadlessMenuItemProps) {
  return (
    <Primitive.Item
      {...props}
      className={cn('flex items-center', className)}
    >
      {startAdornment && (
        <span
          className="flex shrink-0 items-center"
          aria-hidden="true"
        >
          {startAdornment}
        </span>
      )}
      <span className="min-w-0 flex-1">{children}</span>
      {endAdornment && (
        <span
          className="flex shrink-0 items-center"
          aria-hidden="true"
        >
          {endAdornment}
        </span>
      )}
    </Primitive.Item>
  );
}

export const HeadlessMenu = {
  Root: Primitive.Root,
  Trigger,
  Popup,
  Item,
  Group: Primitive.Group,
  GroupLabel: Primitive.GroupLabel,
  Separator: SeparatorPrimitive,
};
